import {
  X,
  ChevronDown,
  ChevronUp,
  Check,
  AlertCircle,
  Loader2,
  FileText,
  Image as ImageIcon,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import {
  useUploadStore,
  selectHasActiveUploads,
  selectCompletedCount,
  selectErrorCount,
  type UploadItem,
} from '@/stores/uploadStore';
import { cn } from '@/lib/utils';

function UploadItemRow({ item, onRemove }: { item: UploadItem; onRemove: () => void }) {
  const Icon = item.type === 'image' ? ImageIcon : FileText;

  return (
    <div className={cn(
      'flex items-center gap-3 p-2 rounded-lg',
      item.status === 'error' && 'bg-destructive/10'
    )}>
      {/* Icon/Status */}
      <div className="flex-shrink-0">
        {item.status === 'done' ? (
          <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
            <Check className="w-4 h-4 text-green-500" />
          </div>
        ) : item.status === 'error' ? (
          <div className="w-8 h-8 rounded-full bg-destructive/20 flex items-center justify-center">
            <AlertCircle className="w-4 h-4 text-destructive" />
          </div>
        ) : item.status === 'uploading' || item.status === 'processing' ? (
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
            <Loader2 className="w-4 h-4 text-primary animate-spin" />
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <Icon className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.fileName}</p>
        {item.status === 'error' ? (
          <p className="text-xs text-destructive truncate">{item.error}</p>
        ) : item.status === 'done' ? (
          <p className="text-xs text-green-500">Fertig</p>
        ) : (
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${item.progress}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground w-8">
              {item.progress}%
            </span>
          </div>
        )}
      </div>

      {/* Remove Button (nur bei Error oder Done) */}
      {(item.status === 'error' || item.status === 'done') && (
        <button
          onClick={onRemove}
          className="p-1 hover:bg-muted rounded"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      )}
    </div>
  );
}

export function GlobalUploadProgress() {
  const uploads = useUploadStore(state => state.uploads);
  const hasActiveUploads = useUploadStore(selectHasActiveUploads);
  const completedCount = useUploadStore(selectCompletedCount);
  const errorCount = useUploadStore(selectErrorCount);
  const isMinimized = useUploadStore(state => state.isMinimized);
  const setMinimized = useUploadStore(state => state.setMinimized);
  const removeUpload = useUploadStore(state => state.removeUpload);
  const clearCompleted = useUploadStore(state => state.clearCompleted);

  // Nichts anzeigen wenn keine Uploads
  if (uploads.length === 0) return null;

  const activeCount = uploads.filter(
    u => u.status === 'pending' || u.status === 'uploading' || u.status === 'processing'
  ).length;

  const totalProgress = uploads.length > 0
    ? Math.round(uploads.reduce((sum, u) => sum + u.progress, 0) / uploads.length)
    : 0;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80">
      <Card className="shadow-lg border-border overflow-hidden">
        {/* Header */}
        <div
          className="flex items-center justify-between p-3 bg-muted/50 cursor-pointer"
          onClick={() => setMinimized(!isMinimized)}
        >
          <div className="flex items-center gap-2">
            {hasActiveUploads ? (
              <Loader2 className="w-4 h-4 text-primary animate-spin" />
            ) : errorCount > 0 ? (
              <AlertCircle className="w-4 h-4 text-destructive" />
            ) : (
              <Check className="w-4 h-4 text-green-500" />
            )}
            <span className="text-sm font-medium">
              {hasActiveUploads
                ? `${activeCount} ${activeCount === 1 ? 'Upload' : 'Uploads'} aktiv`
                : errorCount > 0
                  ? `${errorCount} ${errorCount === 1 ? 'Fehler' : 'Fehler'}`
                  : `${completedCount} ${completedCount === 1 ? 'Upload' : 'Uploads'} abgeschlossen`}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {!hasActiveUploads && uploads.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearCompleted();
                }}
                className="p-1 hover:bg-muted rounded text-xs text-muted-foreground"
              >
                Alle entfernen
              </button>
            )}
            {isMinimized ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Progress Bar (minimiert) */}
        {isMinimized && hasActiveUploads && (
          <div className="h-1 bg-muted">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${totalProgress}%` }}
            />
          </div>
        )}

        {/* Upload Liste (expandiert) */}
        {!isMinimized && (
          <div className="max-h-64 overflow-y-auto p-2 space-y-1">
            {uploads.map(item => (
              <UploadItemRow
                key={item.id}
                item={item}
                onRemove={() => removeUpload(item.id)}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
